/**
 * Pure-logic SVG thumbnail of a city plan. Drawn into the city list cards as
 * a quick visual identity — players recognise their cities at a glance from
 * the rough silhouette of unlocked area + building blob, even though no text
 * labels are involved.
 *
 * Theme-aware: building fills reference the same `--cat-*` CSS variables the
 * Pixi renderer reads, so the thumbnails retint with the rest of the UI when
 * the theme cycles. No memoisation in v1 — each render is sub-millisecond
 * for a few hundred buildings.
 *
 * The thumbnail is a pure visual summary. It does NOT show validation status
 * (overlap / outside / disconnected); buildings outside the unlocked area
 * are drawn at their position anyway. If smoke surfaces a need for a
 * "validation thumbnail" variant, add as a separate exported function.
 */
import type { Plan, ResolvedBuilding } from '$lib/types'

export interface ThumbnailOptions {
  /** Output viewport edge length in CSS pixels. Defaults to 240. */
  size?: number
  /** Padding on every side, in viewport pixels. Defaults to 4. */
  padding?: number
}

const TYPE_TO_VAR: Record<string, string> = {
  residential: '--cat-residential',
  production: '--cat-production',
  random_production: '--cat-production',
  goods: '--cat-goods',
  cultural_goods_production: '--cat-goods',
  culture: '--cat-culture',
  static_provider: '--cat-culture',
  diplomacy: '--cat-culture',
  decoration: '--cat-decoration',
  placement: '--cat-decoration',
  military: '--cat-military',
  tower: '--cat-greatbuilding',
  greatbuilding: '--cat-greatbuilding',
  main_building: '--cat-mainbuilding',
  street: '--cat-street',
  impediment: '--cat-goods',
  clan_power_production: '--cat-greatbuilding',
}

function colourVarFor(type: string | undefined): string {
  if (!type) return '--cat-unknown'
  return TYPE_TO_VAR[type] ?? '--cat-unknown'
}

export function renderThumbnail(
  plan: Plan,
  lookup: (cityentityId: string) => ResolvedBuilding | null,
  options: ThumbnailOptions = {},
): string {
  const size = options.size ?? 240
  const padding = options.padding ?? 4

  // Compute world bounds from unlocked areas. Buildings sitting outside the
  // unlocked area are still drawn — but the bounds are anchored on the
  // areas, so an off-grid drift won't expand the thumbnail viewport (which
  // would shrink the legible blob in the common case for the rare edge case).
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const a of plan.unlockedAreas) {
    if (a.x < minX) minX = a.x
    if (a.y < minY) minY = a.y
    if (a.x + a.width > maxX) maxX = a.x + a.width
    if (a.y + a.length > maxY) maxY = a.y + a.length
  }

  // Empty plan or no unlocked areas: blank thumbnail with a single faint
  // border so the card still has a visual anchor.
  if (!Number.isFinite(minX)) {
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" width="${size}" height="${size}" role="img" aria-label="Empty city">` +
      `<rect x="0.5" y="0.5" width="${size - 1}" height="${size - 1}" fill="none" stroke="var(--border-rule)" stroke-dasharray="2 2"/>` +
      `</svg>`
  }

  const worldW = Math.max(1, maxX - minX)
  const worldH = Math.max(1, maxY - minY)
  const inner = size - padding * 2
  const scale = Math.min(inner / worldW, inner / worldH)
  const drawW = worldW * scale
  const drawH = worldH * scale
  const offsetX = padding + (inner - drawW) / 2
  const offsetY = padding + (inner - drawH) / 2

  const project = (gx: number, gy: number, w: number, h: number) => ({
    x: offsetX + (gx - minX) * scale,
    y: offsetY + (gy - minY) * scale,
    w: w * scale,
    h: h * scale,
  })

  const parts: string[] = []
  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" width="${size}" height="${size}" role="img" aria-label="City layout thumbnail">`,
  )

  // Unlocked area outline — faint, so building blocks pop against it. One
  // path per area; overlapping areas stack visually but the outline reads as
  // the union edge well enough for a thumbnail.
  for (const a of plan.unlockedAreas) {
    const r = project(a.x, a.y, a.width, a.length)
    parts.push(
      `<rect x="${r.x.toFixed(2)}" y="${r.y.toFixed(2)}" width="${r.w.toFixed(2)}" height="${r.h.toFixed(2)}" fill="rgb(var(--surface-base-rgb) / 0.4)" stroke="rgb(var(--border-rule-rgb) / 0.6)" stroke-width="0.5"/>`,
    )
  }

  // Buildings, one rect each. Unknown footprints fall back to 1×1 (matches
  // the renderer's defensive default).
  for (const b of plan.buildings) {
    const resolved = lookup(b.cityentityId)
    const w = resolved && resolved.width > 0 ? resolved.width : 1
    const h = resolved && resolved.height > 0 ? resolved.height : 1
    const r = project(b.x, b.y, w, h)
    const cssVar = colourVarFor(resolved?.type)
    parts.push(
      `<rect x="${r.x.toFixed(2)}" y="${r.y.toFixed(2)}" width="${r.w.toFixed(2)}" height="${r.h.toFixed(2)}" fill="var(${cssVar})"/>`,
    )
  }

  parts.push('</svg>')
  return parts.join('')
}
